import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faIdCard,
  faPhone,
  faUsers,
  faQrcode,
  faTimes,
  faEdit,
  faBan,
} from "@fortawesome/free-solid-svg-icons";

// ============================================
// API CONFIGURATION
// ============================================
// const API_BASE_URL = "https://mhsdev.online/MHS-WEB/api"; // Your PHP backend
export const API_BASE_URL = "https://mhsdev.online/MHS-WEB/api"; // Your PHP backend
export const FAMILIES_API_URL = `${API_BASE_URL}/manageFamiliesCrud.php`;

// ============================================
// VALIDATION PATTERNS (raw digits only — dashes are display-only, see formatters below)
// ============================================
export const VALIDATION_PATTERNS = {
  mobile: /^03\d{9}$/, // Format: 03001234567 (11 digits) -> displayed as 0300-1234567
  cnic: /^\d{13}$/,   // Format: 3460371064171 (13 digits) -> displayed as 34603-7106417-1
  familyName: /^[A-Za-z\s.,'-]{3,50}$/,
  familyCode: /^F-DEA\d{5,}$/,
};

// ============================================
// UTILITY FUNCTIONS
// ============================================
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// ============================================
// NUMBER FIELD HELPERS (auto-dash formatting)
// ============================================
// Strips everything but digits and caps the length. Use this in onChange
// handlers so the value kept in state/sent to the API stays a clean,
// dash-free digit string (what the backend + VALIDATION_PATTERNS expect).
export const toDigitsOnly = (value, maxLength) =>
  (value || "").replace(/\D/g, "").slice(0, maxLength);

// Formats a raw (up to) 13-digit CNIC string for display as 34603-7106417-1
export const formatCnicDisplay = (digitsOnly) => {
  const digits = toDigitsOnly(digitsOnly, 13);
  let formatted = digits.slice(0, 5);
  if (digits.length > 5) formatted += "-" + digits.slice(5, 12);
  if (digits.length > 12) formatted += "-" + digits.slice(12, 13);
  return formatted;
};

// Formats a raw (up to) 11-digit mobile string for display as 0300-1234567
export const formatMobileDisplay = (digitsOnly) => {
  const digits = toDigitsOnly(digitsOnly, 11);
  let formatted = digits.slice(0, 4);
  if (digits.length > 4) formatted += "-" + digits.slice(4, 11);
  return formatted;
};

// ============================================
// DUPLICATE PREVENTION MODAL
// ============================================
export const DuplicatePreventionModal = ({ duplicates, onConfirm, onCancel, onEditExisting }) => {
  if (!duplicates || Object.keys(duplicates).length === 0) return null;

  const duplicateList = Object.values(duplicates).filter(dup => dup.exists);

  return (
    <div className="duplicate-modal-overlay active">
      <div className="duplicate-modal">
        <div className="duplicate-modal-header">
          <div className="duplicate-modal-icon">
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </div>
          <h3>Duplicate Data Detected</h3>
          <p className="duplicate-modal-subtitle">
            The following information already exists in the system:
          </p>
        </div>

        <div className="duplicate-list">
          {duplicateList.map((duplicate, index) => (
            <div key={index} className="duplicate-item">
              <div className="duplicate-field-header">
                <FontAwesomeIcon 
                  icon={duplicate.field === 'cnic' ? faIdCard : faPhone} 
                  className={`duplicate-icon ${duplicate.field}`}
                />
                <span className="duplicate-field-name">
                  {duplicate.field === 'cnic' ? 'CNIC' : 
                   duplicate.field === 'primaryMobile' ? 'Primary Mobile' : 
                   'Secondary Mobile'}
                </span>
              </div>
              <div className="duplicate-value">
                <strong>{duplicate.value}</strong>
              </div>
              <div className="duplicate-existing-family">
                <FontAwesomeIcon icon={faUsers} />
                <span>Existing Family: <strong>{duplicate.existing_family}</strong></span>
              </div>
              <div className="duplicate-existing-code">
                <FontAwesomeIcon icon={faQrcode} />
                <span>Family Code: <strong>{duplicate.existing_code}</strong></span>
              </div>
            </div>
          ))}
        </div>

        <div className="duplicate-action-buttons">
          <button
            className="btn btn-secondary"
            onClick={onCancel}
          >
            <FontAwesomeIcon icon={faTimes} />
            Cancel Submission
          </button>
          
          {onEditExisting && (
            <button
              className="btn btn-primary"
              onClick={onEditExisting}
            >
              <FontAwesomeIcon icon={faEdit} />
              Edit Existing Family
            </button>
          )}
          
          <button
            className="btn btn-danger"
            onClick={onConfirm}
            disabled={duplicateList.length > 0}
          >
            <FontAwesomeIcon icon={faBan} />
            Force Submit Anyway
          </button>
        </div>

        <div className="duplicate-warning">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>
            <strong>Warning:</strong> Submitting duplicate data may cause conflicts in the system.
            It is recommended to edit the existing family or use different information.
          </span>
        </div>
      </div>
    </div>
  );
};